import * as React from 'react';
import { filter, includes, sortBy, padStart, toString, toNumber } from 'lodash';
import { createApplication, WorkstreamTrafficControl, entityConfigs, appSectionConfigs as coreAppSectionConfigs, Icons, graphql, EntityConfigSet } from '@shapeable/core';
import { ApplicationLogo, ApplicationLogoInverted } from './components';
import { themes } from '@shapeable/theme';

import { Edition, SentimentAnalysis, SentimentObservation, SentimentType } from './config';
import { PageDetailsLayout } from './components/page-details-layout';
import { TopicExpertise as TTopicExpertise, Page as TPage } from '@shapeable/gesda-types';

const { Person, FeedEntry, Page, Menu, IndicatorValue, Trend, Topic, SubTopic, Event, TopicExpertise, TrendExpertise, 
  SubTopicExpertise, Space, Citation, Link } = entityConfigs;

const appSectionConfigs = coreAppSectionConfigs;

Page.form ??= {};
Page.form.fieldSets = [
  ...filter(Page.form.fieldSets, (fs) => !includes(['connections'], fs.name)),
  { name: 'connections' },
];

// Page.form.fields.trends = { type: 'linkedMultiple', entityTypeName: 'Trend', fieldSet: 'overview', recommended: false };

Page.form.fields ??= {};
Page.form.fields.connectedPages = { type: 'linkedMultiple', label: 'Connected {{PAGES}}', entityTypeName: 'Page', fieldSet: 'connections', recommended: false, description: 'Select other related {{PAGES}} to show as connected to this {{PAGE}}.' };
Page.form.fields.connectedTrends = { type: 'linkedMultiple', label: 'Connected {{TRENDS}}', entityTypeName: 'Trend', fieldSet: 'connections', recommended: false, description: 'Select other related {{TRENDS}} to show as connected to this {{PAGE}}.' };
Page.form.fields.connectedTopics = { type: 'linkedMultiple', label: 'Connected {{TOPICS}}', entityTypeName: 'Topic', fieldSet: 'connections', recommended: false, description: 'Select other related {{TOPICS}} to show as connected to this {{PAGE}}.' };
Page.form.fields.connectedSubTopics = { type: 'linkedMultiple', label: 'Connected {{SUB_TOPICS}}', entityTypeName: 'SubTopic', fieldSet: 'connections', recommended: false, description: 'Select other related {{SUB_TOPICS}} to show as connected to this {{PAGE}}.' };
Page.form.fields.editions = { type: 'linkedMultiple', entityTypeName: 'Edition', fieldSet: 'overview', recommended: false, description: 'Select the {{EDITIONS}} this {{PAGE}} is associated with.' };
Page.form.fields.doiId = { type: 'text', label: 'Digital Object Identifier (DOI) ID', description: "DOI ID for the {{PAGE}}" };

Page.filters = {
  fields: {
    types: { entityTypeName: 'PageType' },
    editions: { entityTypeName: 'Edition' },
  },
};

if (Link) {
  Link.extraFields = {
    topic: { type: 'linked', entityTypeName: 'Topic', fieldSet: 'target', description: "Alternatively, which {{TOPIC}} does this link target?"  },
    published: { type: 'date', description: 'When was this link published?' },
  }
}

if (IndicatorValue) {
  IndicatorValue.label = 'Indicator Result';
  
  IndicatorValue.extraFields = {
    editions: { type: 'linkedMultiple', label: 'Editions', entityTypeName: 'Edition', description: 'Select the {{EDITIONS}} this {{INDICATOR_RESULT}} is associated with.' },
  }
};

Menu.form ??= {};
Menu.form.fields ??= {};
Menu.form.fields.json = {
  type: 'json',
  fieldSet: 'json',
  recommended: false,
  height: "calc(100vh - 280px)",
  description: "Alternatively, specify a JSON object to describe the structure" 
};

Page.components ??= {};
Page.components.details = PageDetailsLayout;

Person.form ??= {};
Person.form.fields ??= {};
Person.form.fields.hideOnSite = {
  type: 'boolean',
  fieldSet: 'visibility',
  recommended: false,
};

if (Citation) {
  Citation.useSiteSlug = true;
}

FeedEntry.filters ??= {};
FeedEntry.filters.fields = {
  topics: { entityTypeName: 'Topic' },
  subTopics: { entityTypeName: 'SubTopic' },
};

Person.export ||= {};
Person.export.queryFields = graphql`
  name
  email
  latitude
  longitude
  location { name }
  country { name}
  topicExpertise {
    levelOfExpertise { name }
    topic { name }
    editions { slug }
  }
`;

Person.export.worksheets = [
  {
    field: 'topicExpertise',
    groupByField: undefined,
    reverseFields: ['name'],
  }
]

if (TrendExpertise) {
  TrendExpertise.label = 'Scientific Platform Expertise';
}

if (SubTopicExpertise) {
  SubTopicExpertise.label = 'Sub-Field Expertise';
}

if (TopicExpertise) {
  TopicExpertise.label = 'Emerging Topic Expertise';
  TopicExpertise.extraFields = {
    editions: { type: 'linkedMultiple', label: 'Editions', entityTypeName: 'Edition', description: 'Select the editions this level of expertise covers. ' },
  };
}

if (Space) {
  Space.form ??= {};
  Space.form.fieldSets = [
    ...filter(Space.form.fieldSets, (fs) => !includes(['documents'], fs.name)),
    { name: 'documents' },
  ]
  Space.extraFields = {
    title: { type: 'text', description: 'Provide an alternative title to use if the name field is not sufficient.' },
    documentsDescription: { type: 'longText', fieldSet: 'documents', height: '300px', label: 'Description', description: 'Provide a description of the documents that are available' },
  }
}

if (TrendExpertise) {
  TrendExpertise.label = 'Scientific Platform Expertise';
  TrendExpertise.extraFields = {
    editions: { type: 'linkedMultiple', label: 'Editions', entityTypeName: 'Edition', description: 'Select the editions this level of expertise covers. ' },
  };
}

Topic.slug = 'topic';  
Topic.pluralSlug = 'topics';
Topic.extraFields = {
  globalExperts: { label: 'Global Experts', fieldSet: 'community', description: 'Who are the global experts for this {{TOPIC}}?', type: 'linkedMultiple', entityTypeName: 'Person' },
  indicatorValues: { type: 'linkedTable', entityTypeName: 'IndicatorValue', fieldSet: 'indicators', recommended: false },
  surveyObservations: { type: 'longText', fieldSet: 'indicators' },
  trendIntro: { type: 'longText', description: 'Provide a contextual introduction for this {{Topic.label}} when viewed from the {{Trend.label}} page above it' },
  pastYearAdvancements: { label: 'Advancements in the Past Year', type: 'longText' },
  anticipatoryImpact: { label: 'Anticipatory Impact', type: 'longText' },
  anticipatoryImpactImage: { label: 'Anticipatory Impact Image', fieldSet: 'overview', description: 'Setup an image asset for the anticipatory impact radar chart for this {{TOPIC}}', type: 'linked', entityTypeName: 'ImageAsset' },
  connectedPages: { type: 'linkedMultiple', label: 'Connected {{PAGES}}', entityTypeName: 'Page', fieldSet: 'connections', description: 'Select other related {{PAGES}} to show as connected to this {{TOPIC}}.' },
  connectedTrends: { type: 'linkedMultiple', label: 'Connected {{TRENDS}}', entityTypeName: 'Trend', fieldSet: 'connections', description: 'Select other related {{TRENDS}} to show as connected to this {{TOPIC}}.'  },
  connectedTopics: { type: 'linkedMultiple', label: 'Connected {{TOPICS}}', entityTypeName: 'Topic', fieldSet: 'connections', description: 'Select other related {{TOPICS}} to show as connected to this {{TOPIC}}.'  },
  connectedSubTopics: { type: 'linkedMultiple', label: 'Connected {{SUB_TOPICS}}', entityTypeName: 'SubTopic', fieldSet: 'connections', description: 'Select other related {{SUB_TOPICS}} to show as connected to this {{TOPIC}}.'  },
  editions: { type: 'linkedMultiple', label: 'Editions', entityTypeName: 'Edition', fieldSet: 'overview', description: 'Select the {{EDITIONS}} this {{TOPIC}} is associated with.' },
  published: { type: 'date', description: 'What was the date of publication for this {{TOPIC}}?' },
  doiId: { type: 'text', label: 'Digital Object Identifier (DOI) ID', description: "DOI ID for the {{TOPIC}}" },
};

Topic.form ??= {};
Topic.form.fieldSets = [
  ...filter(Topic.form.fieldSets, (fs) => !includes(['indicators'], fs.name)),
  { name: 'indicators' },
];

Topic.details ??= {};
Topic.details.queryFields = graphql`
  expertise {
    ...EntityValueFields
    badge
    levelOfExpertise { ...EntityValueFields orderNumber description { id text } }
    person { ...EntityValueFields }
    latestEdition { ...EntityValueFields name slug numericValue }
  }
`;

Topic.export ||= {};
Topic.export.queryFields = graphql`
  slug
  outlineNumber
  intro { text }
  description { text }
  expertise {
    levelOfExpertise { name }
    person { name }
    editions { slug }
  }
  chapters { name }
  posts { name }
  feedEntries { name url }
  pages { name }
  videos { url }
`;

Topic.export.sortByField = 'outlineNumber';

// transformer to add 2023 edition, to give GESDA a starting point for filling out the sheet  
const topicAddEdition = (record: any) => ({ ...record, 'edition.slug': '2023' });

const sortExpertise = (items: TTopicExpertise[]) => {
  return sortBy(items, (item: TTopicExpertise) => `${3000 - toNumber(item.latestEdition?.numericValue || item.latestEdition?.name)}${padStart(toString(item.levelOfExpertise?.orderNumber), 10, '0')}${item.person?.name}`);
};


Topic.taxonomy ||= {};
Topic.taxonomy.sortExpertise = sortExpertise;

// break out fields
Topic.export.worksheets = [
  {
    field: 'expertise',
    reverseFields: ['outlineNumber', 'name'],
    transformRecord: topicAddEdition,
  },
  {
    field: 'feedEntries',
    fieldLabel: 'Articles',
    reverseFields: ['outlineNumber', 'name'],
  },
  {
    field: 'pages',
    reverseFields: ['outlineNumber', 'name'],
  },
  {
    field: 'videos',
    reverseFields: ['outlineNumber', 'name'],
  },
  // {
  //   field: 'globalExperts',
  //   transformRecord: topicAddEdition,
  //   reverseFields: ['outlineNumber', 'name'],
  // },
  // {
  //   field: 'moderators',
  //   transformRecord: topicAddEdition,
  //   reverseFields: ['outlineNumber', 'name'],
  // },
];

if (appSectionConfigs.challengesGoals) {
  appSectionConfigs.challengesGoals.label = 'GESDA Radar';
}

Trend.form ??= {};
Trend.form.fieldSets = [
  ...filter(Topic.form.fieldSets, (fs) => !includes(['sentiments', 'indicators', 'topics'], fs.name)),
  { name: 'topics' },
  { name: 'sentiments' },
  { name: 'indicators' },
];

Trend.label = 'Theme';
Trend.pluralLabel = 'Themes';
Trend.slug = 'trend';
Trend.pluralSlug = 'trends';
Trend.form.fields ??= {};
Trend.form.fields.sentimentAnalyses = { type: 'linkedTable', entityTypeName: 'SentimentAnalysis', fieldSet: 'sentiments', recommended: false };
Trend.form.fields.indicatorValues = { type: 'linkedTable', entityTypeName: 'IndicatorValue', fieldSet: 'indicators', recommended: false };
Trend.form.fields.dataSetId = { type: 'text', monospace: true, description: "Specify an ID to uniquely identify this {{TREND}}. This might be useful to tie this {{TREND}} to values coming from an external API, for example" };
Trend.form.fields.radarImage = { label: 'Radar Image', description: 'Add the Radar image asset for this {{TREND}}', type: 'linked', entityTypeName: 'ImageAsset' };
Trend.form.fields.connectedPages = { type: 'linkedMultiple', label: 'Connected {{PAGES}}', entityTypeName: 'Page', fieldSet: 'connections', description: 'Select other related {{PAGES}} to show as connected to this {{TREND}}.' };
Trend.form.fields.connectedTrends = { type: 'linkedMultiple', label: 'Connected {{TRENDS}}', entityTypeName: 'Trend', fieldSet: 'connections', description: 'Select other related {{TRENDS}} to show as connected to this {{TREND}}.'  };
Trend.form.fields.connectedTopics = { type: 'linkedMultiple', label: 'Connected {{TOPICS}}', entityTypeName: 'Topic', fieldSet: 'connections', description: 'Select other related {{TOPICS}} to show as connected to this {{TREND}}.'  };
Trend.form.fields.connectedSubTopics = { type: 'linkedMultiple', label: 'Connected {{SUB_TOPICS}}', entityTypeName: 'SubTopic', fieldSet: 'connections', description: 'Select other related {{SUB_TOPICS}} to show as connected to this {{TREND}}.'  };

Trend.form.fields.pulseOfDiplomacyIntro = { type: 'longText', fieldSet: 'overview', description: 'Enter an introduction for the "Pulse of Diplomacy" tab. Briefly highlight key themes and insights related to the trend.' };
Trend.form.fields.pulseOfImpactIntro = { type: 'longText', fieldSet: 'overview', description: 'Enter an introduction for the "Pulse of Impact" tab. Briefly highlight key themes and insights related to the trend.' };
Trend.form.fields.pulseOfImpactContent = { type: 'longText', fieldSet: 'overview', height: '300px', description: 'Enter the content for the "Pulse of Impact" tab.' };
Trend.form.fields.pulseOfSocietyIntro = { type: 'longText', fieldSet: 'overview', description: 'Enter an introduction for the "Pulse of Society" tab. Briefly highlight key themes and insights related to the trend.' };
Trend.form.fields.pulseOfSocietyContent = { type: 'longText', fieldSet: 'overview', height: '300px', description: 'Enter the content for the "Pulse of Society" tab.' };

Trend.form.fields.whatDoPeopleSay = { type: 'linked', label: 'What Do People Say', entityTypeName: 'Page', fieldSet: 'overview', description: 'Select the related What Do People Say {{PAGE}}. This will be shown as a sub-tab within the {{TREND}}' };
Trend.form.fields.whatDoPeopleDo = { type: 'linked', label: 'What Do People Do', entityTypeName: 'Page', fieldSet: 'overview', description: 'Select the related What Do People Do {{PAGE}}. This will be shown as a sub-tab within the {{TREND}}' };
Trend.form.fields.mediaAnalysis = { type: 'linked', label: 'Media Analysis', entityTypeName: 'Page', fieldSet: 'overview', description: 'Select the related Media Analysis {{PAGE}}. This will be shown as a sub-tab within the {{TREND}}' };


SubTopic.form ??= {};
SubTopic.form.fieldSets = [
  ...filter(SubTopic.form.fieldSets, (fs) => !includes(['indicators', 'data'], fs.name)),
  { name: 'indicators' },
];

SubTopic.label = 'Sub-Field';
SubTopic.pluralLabel = 'Sub-Fields';
SubTopic.slug = 'sub-topic';
SubTopic.pluralSlug = 'sub-topics';
SubTopic.form.fields ??= {};
SubTopic.form.fields.indicatorValues = { type: 'linkedTable', entityTypeName: 'IndicatorValue', fieldSet: 'indicators', recommended: false };
SubTopic.form.fields.anticipationScores = { type: 'longText', fieldSet: 'anticipation-scores' };
SubTopic.form.fields.anticipationScoresImage = { type: 'linked', entityTypeName: 'ImageAsset', fieldSet: 'anticipation-scores' };


Event.form ??= {};
Event.form.fieldSets = [
  ...filter(Event.form.fieldSets, (fs) => !includes(['key-takeaways', 'connections', 'documents'], fs.name)),
  { name: 'key-takeaways' },
  { name: 'connections' },
  { name: 'documents' },
];

Event.form.fields ??= {};
Event.form.fields.isKeyTakeawaysReady = { type: 'boolean', label: 'Is Key Takeaways Ready?' , fieldSet: 'key-takeaways', recommended: true, description: 'Is the key takeaways section ready to be published?' };

// need to write a description for keytakeaways that specifies this field is for after (post) the event and to include and quotes from event
Event.form.fields.keyTakeaways = { type: 'longText', label: 'Overview', height: '300px', fieldSet: 'key-takeaways', description: 'Provide an overview of the key takeaways after the event as concluded. Include key quotes from the event.' };
Event.form.fields.desiredOutcomes = { type: 'longText', fieldSet: 'overview' };
Event.form.fields.connectedPages = { type: 'linkedMultiple', label: 'Connected {{PAGES}}', entityTypeName: 'Page', fieldSet: 'connections', description: 'Select other related {{PAGES}} to show as connected to this {{EVENT}}.' };
Event.form.fields.videos = { type: 'linkedMultiple', entityTypeName: 'Video', fieldSet: 'key-takeaways' };
Event.form.fields.editions = { type: 'linkedMultiple', entityTypeName: 'Edition', fieldSet: 'overview' };

Event.form.fields.connectedEvents = { type: 'linkedMultiple', label: 'Connected {{EVENTS}}', entityTypeName: 'Event', fieldSet: 'connections', description: 'Select other related {{EVENTS}} to show as connected to this {{EVENT}}.'  };

Event.form.fields.documentsDescription = { type: 'longText', fieldSet: 'documents', height: '300px', label: 'Description', description: 'Provide a description of the documents that are available - this will only be visible to participating users in the Documents tab of the Event' };

delete Event.form.fields.quotes;
FeedEntry.form ??= {};
FeedEntry.form.fields ??= {};
delete FeedEntry.form.fields.offeringType;

if (appSectionConfigs.community) {
  appSectionConfigs.community.children = filter(appSectionConfigs.community.children, (child: any) => !includes(["chapters"], child.id));
}

const myEntityConfigs = { 
  ...entityConfigs,
  Edition,
  SentimentAnalysis,
  SentimentObservation,
  SentimentType,
};

export const application = createApplication({
  theme: themes.OCEAN_THEME,
  entityConfigs: myEntityConfigs as EntityConfigSet,
  appSectionConfigs,
  options: {
    systemsMapping: 'full',
    shapeLogoLabel: 'oceans',
    shapeLogoIconColor: 'primary',
    shapeLogoIcon: Icons.Platforms.Ecosystems,
  },
  components: {
    Logo: ApplicationLogo,
    LogoInverted: ApplicationLogoInverted,
  },
});


/* ---- Patch Entities definitions afterwards to ensure sub-props are already set --- */

const entities = application.entities;
const entitiesToRemove = entities as EntityConfigSet;

delete entitiesToRemove.ChallengeExpertise;
delete entitiesToRemove.Challenge;
delete entitiesToRemove.KeyIssueExpertise;
delete entitiesToRemove.KeyIssue;
delete entitiesToRemove.SdgExpertise;
delete entitiesToRemove.Sdg;
delete entitiesToRemove.ImpactStory;
delete entitiesToRemove.Program;
delete entitiesToRemove.Course;
delete entitiesToRemove.Podcast;

// -- ensure that traffic control team is not shown in lists
entities.Team.list.filterItems = (items) => filter(items, item => item.slug !== 'traffic-control');

// -- show Traffic controllers
entities.Team.list.postBanner = <WorkstreamTrafficControl />;
